import { useState, useEffect } from "react";
import { Mail, ArrowLeft, CheckCircle, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import Header from "../components/user/Header";
import Footer from "../components/user/Footer";

import { useUserAuth } from "../services/user/UserAuthServices";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState(1); // 1: Email, 2: OTP
  const [countdown, setCountdown] = useState(0);
  const { forgotPasswordAPI, verifyOtpAPI, loading, error, clearError } = useUserAuth();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    if (!email) {
      return;
    }

    const result = await forgotPasswordAPI(email);
    if (result.status === "success") {
      setStep(2);
      setCountdown(60);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!otp || otp.length !== 6) {
      return;
    }

    const result = await verifyOtpAPI(email, otp);
    if (result.status === "success") {
      window.location.href = `/reset-password?email=${encodeURIComponent(email)}&token=${result.verification_token}`;
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#121212] flex flex-col">
      <Header />

      <main className="flex-1 flex items-center justify-center p-4 py-12 md:py-20 bg-gradient-to-br from-[#F5F3FF] via-white to-[#FAF5FF] dark:from-[#0A0A0A] dark:via-[#111111] dark:to-[#1A1A1A] relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-[#62397f]/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-[#7C3AED]/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="w-full max-w-md relative animate-fade-in">
          {/* Back Link */}
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-[#62397f] dark:text-[#A78BFA] hover:underline mb-8 font-medium group transition-all"
          >
            <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
            <span>Back to Login</span>
          </Link>

          {/* Card Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-[#62397f] to-[#7C3AED] flex items-center justify-center shadow-xl mx-auto mb-4 transform hover:scale-105 transition-transform duration-300">
              <img src="/static/images/logo.webp" alt="St. Stephen's Rentals Logo" className="w-16 h-16 object-contain" />
            </div>
            <h1 className="text-3xl font-medium font-serif text-[#111111] dark:text-white mb-2">
              {step === 1 ? "Forgot Password?" : "Verify OTP"}
            </h1>
            <p className="text-[#666666] dark:text-[#A0A0A0]">
              {step === 1
                ? "Enter your email for a reset code"
                : "A 6-digit code has been sent to your email"}
            </p>
          </div>

          {/* Card */}
          <div className="bg-white dark:bg-[#1A1A1A] rounded-3xl shadow-2xl border border-[#E5E7EB] dark:border-[#2A2A2A] p-8 md:p-10">
            {step === 1 ? (
              <form onSubmit={handleSendOTP} className="space-y-6">
                {error && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 animate-fade-in">
                    <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
                  </div>
                )}

                <div className="space-y-2">
                  <label className="block text-sm font-medium text-[#111111] dark:text-white ml-1">
                    Email Address
                  </label>
                  <div className="relative group">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-[#999999] group-focus-within:text-[#62397f] transition-colors" size={18} />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-12 pr-4 py-3.5 bg-[#F9FAFB] dark:bg-[#0A0A0A] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-2xl text-[#111111] dark:text-white placeholder-[#999999] focus:outline-none focus:ring-2 focus:ring-[#62397f]/50 focus:border-[#62397f] transition-all"
                      placeholder="your@email.com"
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 px-6 bg-gradient-to-r from-[#62397f] to-[#7C3AED] text-white font-bold rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:transform-none"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Sending OTP...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      Send OTP <ArrowRight size={18} />
                    </span>
                  )}
                </button>
              </form>
            ) : (
              <div className="space-y-6">
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-2xl p-4 animate-fade-in flex items-start gap-3">
                  <CheckCircle className="text-green-600 dark:text-green-400 mt-0.5 shrink-0" size={20} />
                  <div>
                    <h3 className="text-green-800 dark:text-green-300 font-bold text-sm">OTP Sent Successfully</h3>
                    <p className="text-green-700 dark:text-green-400 text-xs mt-1">
                      Check your inbox at <span className="font-semibold">{email}</span>
                    </p>
                  </div>
                </div>

                <form onSubmit={handleVerifyOTP} className="space-y-6">
                  {error && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 animate-fade-in">
                      <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
                    </div>
                  )}

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-[#111111] dark:text-white text-center">
                      Enter 6-Digit Code
                    </label>
                    <input
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      className="w-full px-4 py-4 bg-[#F9FAFB] dark:bg-[#0A0A0A] border border-[#E5E7EB] dark:border-[#2A2A2A] rounded-2xl text-[#111111] dark:text-white text-center text-3xl font-mono tracking-[0.5em] placeholder-[#CCCCCC] focus:outline-none focus:ring-2 focus:ring-[#62397f]/50 focus:border-[#62397f] transition-all"
                      placeholder="000000"
                      maxLength={6}
                      required
                    />
                  </div>

                  <div className="text-center">
                    <button
                      type="button"
                      disabled={countdown > 0 || loading}
                      className="text-sm font-semibold text-[#62397f] dark:text-[#A78BFA] hover:underline disabled:opacity-50 disabled:no-underline"
                    >
                      {countdown > 0 ? `Resend code in ${countdown}s` : "Resend code"}
                    </button>
                  </div>

                  <button
                    type="submit"
                    disabled={loading || otp.length !== 6}
                    className="w-full py-4 px-6 bg-gradient-to-r from-[#62397f] to-[#7C3AED] text-white font-bold rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:transform-none"
                  >
                    {loading ? "Verifying..." : "Verify Code"}
                  </button>

                  <button
                    type="button"
                    onClick={() => { setStep(1); setOtp(""); clearError(); }}
                    className="w-full text-[#666666] dark:text-[#A0A0A0] text-sm hover:text-[#111111] dark:hover:text-white transition-colors"
                  >
                    Use a different email address
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />

      <style jsx="true" global="true">{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.6s ease-out both;
        }
      `}</style>
    </div>
  );
}
